// ============================================================================
// GitHub 연동 API 서비스 (CHG-046)
// ============================================================================

import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---

export interface GitHubRepo {
  repoId: string;
  owner: string;
  name: string;
  fullName: string;
  defaultBranch: string;
  branchStrategy: string;
  isActive: boolean;
  webhookId: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; name: string; email: string };
  _count: { branches: number; commits: number; pullRequests: number };
}

export interface ConnectRepoData {
  owner: string;
  name: string;
  accessToken: string;
  branchStrategy: string;
  defaultBranch?: string;
}

export interface UpdateRepoData {
  branchStrategy?: string;
  defaultBranch?: string;
  isActive?: boolean;
  accessToken?: string;
}

export interface GitHubStatusMappingData {
  githubEvent: string;
  targetStatusId: string | null;
  isEnabled: boolean;
}

export interface SyncResult {
  commits: number;
  pullRequests: number;
  branches: number;
}

export interface TestResult {
  status: 'ok' | 'error';
  message?: string;
  repoInfo?: {
    fullName: string;
    defaultBranch: string;
    private: boolean;
    language: string | null;
    updatedAt: string;
  };
}

// --- Repository CRUD ---

export async function fetchGitHubRepos(projectId: string): Promise<GitHubRepo[]> {
  const res = await api.get<ApiResponse<GitHubRepo[]>>(`/projects/${projectId}/github/repos`);
  return res.data.data!;
}

export async function connectGitHubRepo(projectId: string, data: ConnectRepoData) {
  const res = await api.post<ApiResponse<GitHubRepo>>(`/projects/${projectId}/github/repos`, data);
  return res.data.data!;
}

export async function updateGitHubRepo(projectId: string, repoId: string, data: UpdateRepoData) {
  const res = await api.put<ApiResponse<GitHubRepo>>(`/projects/${projectId}/github/repos/${repoId}`, data);
  return res.data.data!;
}

export async function disconnectGitHubRepo(projectId: string, repoId: string) {
  const res = await api.delete<ApiResponse>(`/projects/${projectId}/github/repos/${repoId}`);
  return res.data;
}

export async function testGitHubConnection(projectId: string, repoId: string): Promise<TestResult> {
  const res = await api.post<ApiResponse<TestResult>>(`/projects/${projectId}/github/repos/${repoId}/test`);
  return res.data.data!;
}

export async function syncGitHubRepo(projectId: string, repoId: string): Promise<SyncResult> {
  const res = await api.post<ApiResponse<SyncResult>>(`/projects/${projectId}/github/repos/${repoId}/sync`);
  return res.data.data!;
}

// --- Status Mapping ---

export interface StatusMappingResponse {
  mappings: {
    mappingId: string;
    githubEvent: string;
    targetStatusId: string | null;
    targetStatusName: string | null;
    targetStatusValid: boolean;
    isEnabled: boolean;
  }[];
  statuses: {
    statusId: string;
    name: string;
    color: string;
    category: string;
  }[];
}

export async function fetchStatusMappings(projectId: string): Promise<StatusMappingResponse> {
  const res = await api.get<ApiResponse<StatusMappingResponse>>(`/projects/${projectId}/github/status-mappings`);
  return res.data.data!;
}

export async function saveStatusMappings(projectId: string, mappings: GitHubStatusMappingData[]) {
  const res = await api.put<ApiResponse>(`/projects/${projectId}/github/status-mappings`, { mappings });
  return res.data;
}

// --- User Profile ---

export async function fetchUserProfile() {
  const res = await api.get<ApiResponse<{ id: string; email: string; name: string; githubUsername: string | null }>>('/users/me/profile');
  return res.data.data!;
}

export async function updateUserProfile(data: { name?: string; githubUsername?: string | null }) {
  const res = await api.put<ApiResponse<{ id: string; email: string; name: string; githubUsername: string | null }>>('/users/me/profile', data);
  return res.data.data!;
}

// --- Issue-level GitHub data ---

export interface GitHubBranchItem {
  branchId: string;
  name: string;
  sourceBranch: string | null;
  status: 'ACTIVE' | 'MERGED' | 'DELETED';
  repoFullName: string;
  githubUrl: string;
  createdAt: string;
}

export interface GitHubCommitItem {
  commitId: string;
  sha: string;
  shortSha: string;
  message: string;
  author: {
    name: string;
    githubUsername: string | null;
    maiosUser?: { userId: string; name: string; profileImage: string | null };
  };
  additions: number;
  deletions: number;
  filesChanged: number;
  committedAt: string;
  githubUrl: string;
  linkType: 'REFERENCE' | 'FIXES';
}

export interface GitHubPRReviewItem {
  reviewerGithub: string;
  reviewerUser?: { userId: string; name: string; profileImage: string | null };
  state: 'PENDING' | 'APPROVED' | 'CHANGES_REQUESTED' | 'COMMENTED' | 'DISMISSED';
  submittedAt: string | null;
}

export interface GitHubPRItem {
  prId: string;
  prNumber: number;
  title: string;
  status: 'OPEN' | 'MERGED' | 'CLOSED' | 'DRAFT';
  isDraft: boolean;
  sourceBranch: string;
  targetBranch: string;
  author: {
    githubUsername: string | null;
    maiosUser?: { userId: string; name: string; profileImage: string | null };
  };
  additions: number;
  deletions: number;
  filesChanged: number;
  hasConflicts: boolean;
  ciStatus: 'PENDING' | 'SUCCESS' | 'FAILURE' | 'ERROR' | null;
  reviews: GitHubPRReviewItem[];
  mergedAt: string | null;
  closedAt: string | null;
  githubUrl: string;
  repoFullName?: string;
  createdAt: string;
  updatedAt: string;
}

export async function fetchIssueBranches(projectId: string, issueId: string): Promise<GitHubBranchItem[]> {
  const res = await api.get<ApiResponse<GitHubBranchItem[]>>(`/projects/${projectId}/issues/${issueId}/github/branches`);
  return res.data.data!;
}

export async function fetchIssueCommits(projectId: string, issueId: string): Promise<GitHubCommitItem[]> {
  const res = await api.get<ApiResponse<GitHubCommitItem[]>>(`/projects/${projectId}/issues/${issueId}/github/commits`);
  return res.data.data!;
}

export async function fetchIssuePullRequests(projectId: string, issueId: string): Promise<GitHubPRItem[]> {
  const res = await api.get<ApiResponse<GitHubPRItem[]>>(`/projects/${projectId}/issues/${issueId}/github/pull-requests`);
  return res.data.data!;
}

export async function createIssueBranch(projectId: string, issueId: string, data: { repoId: string; sourceBranch?: string; customName?: string }) {
  const res = await api.post<ApiResponse>(`/projects/${projectId}/issues/${issueId}/github/branches`, data);
  return res.data.data;
}

export async function createIssuePR(projectId: string, issueId: string, data: {
  repoId: string; sourceBranch: string; targetBranch?: string; title?: string; body?: string; isDraft?: boolean; reviewers?: string[];
}) {
  const res = await api.post<ApiResponse>(`/projects/${projectId}/issues/${issueId}/github/pull-requests`, data);
  return res.data.data;
}

// --- Review Dashboard ---

export interface ReviewDashboardSummary {
  summary: {
    waiting: number;
    inReview: number;
    approved: number;
    changesRequested: number;
    slaViolations: number;
  };
  recentActivity: {
    mergedToday: number;
    mergedThisWeek: number;
    avgReviewTime: number;
    avgLeadTime: number;
  };
}

export interface ReviewDashboardPR {
  prId: string;
  prNumber: number;
  title: string;
  status: string;
  isDraft: boolean;
  sourceBranch: string;
  targetBranch: string;
  author: { githubUsername: string | null; maiosUser?: { userId: string; name: string; profileImage: string | null } };
  additions: number;
  deletions: number;
  filesChanged: number;
  hasConflicts: boolean;
  ciStatus: string | null;
  githubUrl: string;
  repoFullName: string;
  issue: { id: string; issueKey: string; title: string } | null;
  reviews: { reviewerGithub: string; reviewerUser?: { userId: string; name: string; profileImage: string | null }; state: string; submittedAt: string | null }[];
  reviewStatus: 'waiting' | 'inReview' | 'approved' | 'changesRequested';
  waitingHours: number;
  slaViolated: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewerWorkload {
  userId: string | null;
  name: string;
  profileImage: string | null;
  githubUsername: string;
  // CHG-133 Step 7: MaioS user인 경우 표시 키
  displayColor?: string | null;
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  stats: {
    pendingReviews: number;
    completedThisWeek: number;
    completedThisMonth: number;
    avgResponseTimeHours: number;
  };
  pendingPRs: { prId: string; prNumber: number; title: string; waitingHours: number; slaViolated: boolean; repoFullName: string }[];
}

export async function fetchReviewDashboardSummary(projectId: string): Promise<ReviewDashboardSummary> {
  const res = await api.get<ApiResponse<ReviewDashboardSummary>>(`/projects/${projectId}/github/review-dashboard`);
  return res.data.data!;
}

export async function fetchReviewDashboardPRs(projectId: string, params?: Record<string, string>) {
  const res = await api.get<{ success: boolean; data: ReviewDashboardPR[]; total: number; page: number; limit: number; totalPages: number }>(`/projects/${projectId}/github/review-dashboard/prs`, { params });
  return res.data;
}

export async function fetchReviewerWorkload(projectId: string): Promise<{ reviewers: ReviewerWorkload[] }> {
  const res = await api.get<ApiResponse<{ reviewers: ReviewerWorkload[] }>>(`/projects/${projectId}/github/review-dashboard/reviewers`);
  return res.data.data!;
}

// --- Project settings (branchNamingPattern, commitKeywords) ---

export async function updateProjectGitHubSettings(projectId: string, data: {
  branchNamingPattern?: string;
  commitKeywords?: Record<string, string | null>;
  reviewSlaHours?: number;
} | Record<string, unknown>) {
  const res = await api.put<ApiResponse>(`/projects/${projectId}`, data);
  return res.data;
}
